import { LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { appRootStyles } from './app-root.css';
import { renderAppRootPresentation, type AppRootProps } from './app-root-presentation';
import { getAllPosts, getDocBySlug, MarkdownDoc } from '../../data/markdownEngine';
import { analyticsService } from '../../services/analyticsService';
import { metaService } from '../../services/metaService';

import '../app-header/app-header';
import '../app-nav/app-nav';
import '../post-list/post-list';
import '../project-grid/project-grid';
import '../post-reader/post-reader';
import '../about-view/about-view';
import '../analytics-view/analytics-view';
import '../app-footer/app-footer';

@customElement('app-root')
export class AppRoot extends LitElement {
  static styles = [appRootStyles];

  @state() private activeTab = 'about';
  @state() private activeDoc: MarkdownDoc | null = null;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.handleRouteFromUrl.bind(this));
    this.handleRouteFromUrl();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handleRouteFromUrl.bind(this));
    super.disconnectedCallback();
  }

  private handleRouteFromUrl() {
    const rawPath = window.location.pathname.replace(/\/$/, '') || '/';
    const hashSlug = window.location.hash.replace(/^#\/?(posts\/|projects\/)?/, '');

    // Support clean URL path /posts/:slug (e.g. /posts/cost-of-generation)
    if (rawPath.startsWith('/posts/')) {
      const slug = rawPath.replace('/posts/', '');
      if (slug) {
        const foundDoc = getDocBySlug(slug);
        if (foundDoc) {
          this.activeDoc = foundDoc;
          analyticsService.trackPageView(`/posts/${foundDoc.slug}`, foundDoc.title);
          metaService.updateMeta({
            title: foundDoc.title,
            description: foundDoc.summary,
            image: foundDoc.coverImage,
            url: `https://www.geireann.com/posts/${foundDoc.slug}`,
          });
          return;
        }
      }
    }

    // Support legacy hash links if navigated to directly
    if (hashSlug && hashSlug !== 'about' && hashSlug !== 'posts') {
      const foundDoc = getDocBySlug(hashSlug);
      if (foundDoc) {
        this.activeDoc = foundDoc;
        window.history.replaceState({ slug: foundDoc.slug }, '', `/posts/${foundDoc.slug}`);
        analyticsService.trackPageView(`/posts/${foundDoc.slug}`, foundDoc.title);
        metaService.updateMeta({
          title: foundDoc.title,
          description: foundDoc.summary,
          image: foundDoc.coverImage,
          url: `https://www.geireann.com/posts/${foundDoc.slug}`,
        });
        return;
      }
    }

    if (rawPath === '/analytics') {
      this.activeTab = 'analytics';
      this.activeDoc = null;
      return;
    }

    if (rawPath === '/posts' || hashSlug === 'posts') {
      this.activeTab = 'posts';
      this.activeDoc = null;
      analyticsService.trackPageView('/posts', 'Posts');
      metaService.updateMeta({ title: 'Posts & Writings', url: 'https://www.geireann.com/posts' });
    } else {
      this.activeTab = 'about';
      this.activeDoc = null;
      analyticsService.trackPageView('/about', 'About');
      metaService.updateMeta({ title: 'About', url: 'https://www.geireann.com/about' });
    }
  }

  private handleNavChange(tab: string) {
    this.activeTab = tab;
    this.activeDoc = null;
    const targetUrl = tab === 'about' ? '/about' : `/${tab}`;
    window.history.pushState(null, '', targetUrl);
    analyticsService.trackPageView(targetUrl, tab.toUpperCase());
    metaService.updateMeta({ title: tab.toUpperCase(), url: `https://www.geireann.com${targetUrl}` });
  }

  private handleOpenDoc(doc: MarkdownDoc) {
    this.activeDoc = doc;
    const cleanUrl = `/posts/${doc.slug}`;
    window.history.pushState({ slug: doc.slug }, '', cleanUrl);
    analyticsService.trackPageView(cleanUrl, doc.title);
    metaService.updateMeta({
      title: doc.title,
      description: doc.summary,
      image: doc.coverImage,
      url: `https://www.geireann.com${cleanUrl}`,
    });
  }

  private handleBackToList() {
    this.activeDoc = null;
    const targetUrl = `/${this.activeTab}`;
    window.history.pushState(null, '', targetUrl);
    analyticsService.trackPageView(targetUrl, this.activeTab.toUpperCase());
    metaService.updateMeta({ title: this.activeTab.toUpperCase(), url: `https://www.geireann.com${targetUrl}` });
  }

  render() {
    const posts = getAllPosts();

    const props: AppRootProps = {
      activeTab: this.activeTab,
      activeDoc: this.activeDoc,
      posts,
      projects: posts,
      investigations: posts,
      onNavChange: (tab: string) => this.handleNavChange(tab),
      onOpenDoc: (doc: MarkdownDoc) => this.handleOpenDoc(doc),
      onBackToList: () => this.handleBackToList(),
    };

    return renderAppRootPresentation(props);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-root': AppRoot;
  }
}
